import React from "react";
import { useField } from "formik";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const InputField = ({
  disable,
  required,
  label,
  isPassword,
  showPassword,
  onPasswordToggle,
  isCheckbox,
  isToggle,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);

  return (
    <div className="form-control w-full">
      {/* Label - Only show if not checkbox/toggle */}
      {!isCheckbox && !isToggle && (
        <label
          htmlFor={props.id || props.name}
          className="block text-sm font-medium text-gray-200 mb-1"
        >
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      {/* Checkbox/Toggle Input */}
      {(isCheckbox || isToggle) && (
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              {...field}
              {...props}
              checked={field.value}
              onChange={(e) => helpers.setValue(e.target.checked)}
            />
            <div className={`
              relative w-11 h-6 
              bg-gray-200 peer-focus:outline-none 
              rounded-full peer 
              peer-checked:after:translate-x-full 
              peer-checked:after:border-white 
              after:content-[''] 
              after:absolute after:top-[2px] after:left-[2px] 
              after:bg-white after:border-gray-300 after:border 
              after:rounded-full after:h-5 after:w-5 
              after:transition-all 
              ${field.value ? 'bg-primary' : 'bg-gray-200'}
            `}></div>
            <span className="ml-3 text-sm font-medium text-gray-200">
              {label}
              {required && <span className="text-red-400 ml-1">*</span>}
            </span>
          </label>
        </div>
      )}

      {/* Regular Input Field */}
      {!isCheckbox && !isToggle && (
        <div className="relative">
          <input
            className={`
              w-full px-4 py-2.5 text-sm
              border border-gray-600 rounded-lg
              focus:ring-2 focus:ring-primary-500/50
              focus:border-primary-500
              transition-all duration-200
              placeholder:text-gray-400
              text-white
              ${disable ? 'bg-gray-700 cursor-not-allowed' : 'bg-gray-800 hover:border-gray-500'}
              ${meta.touched && meta.error ? 'border-red-500 focus:ring-red-500/30 focus:border-red-500' : 'border-gray-600'}
              ${isPassword ? 'pr-12' : ''}
              ${props.className && props.className.includes('pl-12') ? '' : ''}
              ${props.className || ''}
            `}
            {...field}
            {...props}
            autoComplete="off"
            disabled={disable}
            type={isPassword && !showPassword ? "password" : props.type || "text"}
            aria-invalid={meta.touched && meta.error ? 'true' : 'false'}
            aria-describedby={meta.touched && meta.error ? `${props.name}-error` : undefined}
          />

          {/* Password Toggle */}
          {isPassword && (
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 focus:outline-none focus:text-primary-400 transition-colors duration-200"
              onClick={onPasswordToggle}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <FaEyeSlash className="h-5 w-5" />
              ) : (
                <FaEye className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {meta.touched && meta.error && (
        <p
          id={`${props.name}-error`}
          className="mt-1 text-sm text-red-400 flex items-center gap-1"
          role="alert"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {meta.error}
        </p>
      )}
    </div>
  );
};

export default InputField;
